import { useScript } from "usehooks-ts";
import { Button } from "../core/basic/Button";
import { useEffect, type ComponentProps } from "react";
import { AMAZON_AUTH_APP_ID } from "../../utils/gvSecrets";
import { css, cx } from "../../../styled-system/css";

type AuthorizeResponse = { code: string; state?: string; error?: never };
type TokenResponse = { success: true; access_token: string; expires_in: number };
type ProfileResponse = {
  CustomerId: string;
  Name: string;
  PrimaryEmail: string;
};

interface AmazonLwaSdk {
  Login: {
    setClientId: (clientId: string) => void;
    authorize: (
      options: {
        scope: string | string[];
        response_type: "code";
        pkce: true;
        interactive?: "always" | "never" | "auto";
      },
      next: (res: AuthorizeResponse | { error: string; error_description: string; error_uri: string }) => void,
    ) => void;
    retrieveToken: (code: string, next: (res: TokenResponse | { success: false; error: string }) => void) => void;
    retrieveProfile: (
      accessToken: string,
      next: (res: { success: true; profile: ProfileResponse } | { success: false; error: string }) => void,
    ) => void;
  };
}

declare global {
  interface Window {
    onAmazonLoginReady: () => void;
    amazon: AmazonLwaSdk;
  }
}

export interface LWAResult {
  accessToken: string;
  email: string;
}

interface Props {
  onSuccess: (result: LWAResult) => void;
  onError: (error: Error) => void;
  className?: string;
}

export function LWAButton({ onSuccess, onError, className, ...rest }: Props & ComponentProps<typeof Button>) {
  const sdkStatus = useScript("https://assets.loginwithamazon.com/sdk/na/login1.js");

  useEffect(() => {
    window.onAmazonLoginReady = () => {
      window.amazon.Login.setClientId(AMAZON_AUTH_APP_ID);
    };
  }, []);

  return (
    <Button
      isDisabled={sdkStatus !== "ready"}
      onPress={async () => {
        try {
          const res = await amazonSdkAuthorize();
          const tokenResponse = await amazonSdkGetTokenFromCode(res.code);
          const profile = await amazonSdkGetProfile(tokenResponse.access_token);
          onSuccess({
            accessToken: tokenResponse.access_token,
            email: profile.PrimaryEmail,
          });
        } catch (error) {
          onError(error instanceof Error ? error : new Error("Unknown error during Amazon login"));
        }
      }}
      className={cx(
        css({
          width: "100%",
          fontSize: "small",
          letterSpacing: "unset",
          textTransform: "none",
        }),
        className,
      )}
      {...rest}
    />
  );
}

async function amazonSdkAuthorize() {
  return await new Promise<AuthorizeResponse>((resolve, reject) => {
    window.amazon.Login.authorize(
      { scope: "profile", response_type: "code", pkce: true, interactive: "always" },
      (res) => {
        if (res.error) {
          reject(new Error(res.error, { cause: res }));
        } else {
          resolve(res as AuthorizeResponse);
        }
      },
    );
  });
}

async function amazonSdkGetTokenFromCode(code: string) {
  return await new Promise<TokenResponse>((resolve, reject) => {
    window.amazon.Login.retrieveToken(code, (res) => {
      if (res.success) {
        resolve(res);
      } else {
        reject(new Error(res.error));
      }
    });
  });
}

async function amazonSdkGetProfile(accessToken: string) {
  return await new Promise<ProfileResponse>((resolve, reject) => {
    window.amazon.Login.retrieveProfile(accessToken, (res) => {
      if (res.success) {
        resolve(res.profile);
      } else {
        reject(new Error(res.error));
      }
    });
  });
}
